import { useId } from "react";
import { CURRENCIES, FOUND_VIA, WORK_TYPES, type WinInput, type WinPaid } from "../../api/gigboard";
import { Field, inputShell } from "../../components/Field";
import { numberOrNull } from "../../lib/format";
import { buttonClass } from "../../components/Button";

/** The Report a Win fields as typed, before validation. Shared by the Report a Win screen and the Friday check-in. */
export interface WinDraft {
  what: string;
  type: string;
  via: string;
  start: string;
  paid: WinPaid | "";
  amount: string;
  currency: string;
}

export const today = () => new Date().toISOString().slice(0, 10);

export function emptyWin(prefill?: Partial<WinDraft>): WinDraft {
  return {
    what: "",
    type: "",
    via: "",
    start: today(),
    paid: "",
    amount: "",
    currency: "NGN",
    ...prefill,
  };
}

/** v2's wording: one sentence naming everything still missing. Null when the win can be saved. */
export function winError(draft: WinDraft): string | null {
  const missing = [
    !draft.what.trim() && "say what the work is",
    !draft.type && "choose the type of work",
    !draft.via && "say how you found it",
    !draft.paid && "say whether you have been paid",
  ].filter(Boolean);
  return missing.length ? `Please ${missing.join(", ")}.` : null;
}

export function toWinInput(draft: WinDraft, applicationId?: string | null): WinInput {
  return {
    what: draft.what.trim(),
    type: draft.type,
    via: draft.via,
    startDate: draft.start || null,
    paid: draft.paid as WinPaid,
    amount: numberOrNull(draft.amount),
    currency: draft.currency,
    applicationId: applicationId ?? null,
  };
}

const PAID: [WinPaid, string][] = [
  ["yes", "Yes, paid"],
  ["part", "Part-paid or deposit"],
  ["notyet", "Not yet"],
];

const selectShell = `${inputShell} min-h-11`;

/** The win fields, controlled. Proof is added by the Report a Win screen (the check-in's win has none). */
export function WinFields({
  value,
  onChange,
}: {
  value: WinDraft;
  onChange: (next: WinDraft) => void;
}) {
  const id = useId();
  const set = (patch: Partial<WinDraft>) => onChange({ ...value, ...patch });
  return (
    <div>
      <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
        <Field label="What's the work?" htmlFor={`${id}-what`}>
          <input
            id={`${id}-what`}
            type="text"
            className={inputShell}
            value={value.what}
            placeholder="e.g. Logo and labels for a pepper sauce brand"
            onChange={(event) => set({ what: event.target.value })}
          />
        </Field>
        <Field label="Type of work" htmlFor={`${id}-type`}>
          <select
            id={`${id}-type`}
            className={selectShell}
            value={value.type}
            onChange={(event) => set({ type: event.target.value })}
          >
            <option value="">Choose...</option>
            {WORK_TYPES.map((type) => (
              <option key={type}>{type}</option>
            ))}
          </select>
        </Field>
        <Field label="How did you find it?" htmlFor={`${id}-via`}>
          <select
            id={`${id}-via`}
            className={selectShell}
            value={value.via}
            onChange={(event) => set({ via: event.target.value })}
          >
            <option value="">Choose...</option>
            {FOUND_VIA.map((via) => (
              <option key={via}>{via}</option>
            ))}
          </select>
        </Field>
        <Field label="Start date" htmlFor={`${id}-start`}>
          <input
            id={`${id}-start`}
            type="date"
            className={inputShell}
            value={value.start}
            onChange={(event) => set({ start: event.target.value })}
          />
        </Field>
      </div>
      <div className="mb-4 flex flex-col gap-1.5">
        <span id={`${id}-paid`} className="text-[12.5px] font-bold text-text">
          Have you been paid for it?
        </span>
        <div role="group" aria-labelledby={`${id}-paid`} className="flex flex-wrap gap-2">
          {PAID.map(([paid, label]) => {
            const on = value.paid === paid;
            return (
              <button
                key={paid}
                type="button"
                aria-pressed={on}
                onClick={() => set({ paid })}
                className={buttonClass(on ? "primary" : "ghost", "sm")}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
        <Field label="Amount (optional)" htmlFor={`${id}-amount`}>
          <input
            id={`${id}-amount`}
            type="number"
            min="0"
            inputMode="decimal"
            className={inputShell}
            value={value.amount}
            onChange={(event) => set({ amount: event.target.value })}
          />
        </Field>
        <Field label="Currency" htmlFor={`${id}-currency`}>
          <select
            id={`${id}-currency`}
            className={selectShell}
            value={value.currency}
            onChange={(event) => set({ currency: event.target.value })}
          >
            {CURRENCIES.map((currency) => (
              <option key={currency}>{currency}</option>
            ))}
          </select>
        </Field>
      </div>
    </div>
  );
}
